import {
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output
} from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';

import type { Component as AmChartsComponent, Slice } from '@amcharts/amcharts4/core';
import type { PieChart } from '@amcharts/amcharts4/.internal/charts/types/PieChart';

import { IOnChartReadyData } from '../amChartsCore/VbUiAmChartsCore.Component';
import { getCommonLegendConfig, getBottomLegendConfig } from '../amChartsCore/LegendConfig';

import { deepMergeObjects } from 'rev-shared/util/index';

import styles from './VbUiPieChart.Component.module.less';
import { ThemeService } from 'rev-portal/branding/Theme.Service';

export interface IOnSliceHitData {
	data: any;
}

const LEGEND_ITEM_PADDING: number = 2;

@Component({
	selector: 'vb-ui-pie-chart',
	host: {
		'[class]': 'styles.pieChart'
	},
	template: `
		<vb-ui-am-charts-core
			[chartConfig]="chartConfig"
			[chartType]="chartType"
			[data]="data"
			[updateEnabled]="updateEnabled"
			[scrollableLegend]="scrollableLegend"
			[legendCssClass]="legendCssClass"
			(onChartReady)="onChartReadyInternal($event)">
		</vb-ui-am-charts-core>
	`,
	standalone: false
})
export class VbUiPieChartComponent implements OnInit {
	@Input() public categoryField: string;
	@Input() public updateEnabled: boolean;
	@Input() public data: any;
	@Input() public valueField: string;
	@Input() public disableLegendToggle: boolean;
	@Input() public internalLabels: boolean;
	@Input() public bottomLegend: boolean;
	@Input() public scrollableLegend: boolean;
	@Input() public outline: boolean;
	@Input() public legendCssClass: string;

	@Output() public onChartReady = new EventEmitter<IOnChartReadyData>();
	@Output() public onSliceHit = new EventEmitter<IOnSliceHitData>();

	constructor(private readonly ThemeService: ThemeService) {}

	private chart: AmChartsComponent;
	public chartConfig: any;
	public chartType: typeof PieChart;
	public readonly styles = styles;

	public ngOnInit(): void {
		Promise.all([
			import(/* webpackChunkName: "amChartsPieChart" */ '@amcharts/amcharts4/.internal/charts/types/PieChart'),
			import(/* webpackChunkName: "amChartsNumberFormatter" */ '@amcharts/amcharts4/.internal/core/formatters/NumberFormatter')
		])
			.then(([pieChartExports]) => {
				this.chartType = (pieChartExports as any).PieChart; // TODO: TypeScript 3.7.3 workaround
				this.initChartConfig();
			});
	}

	private initChartConfig(): void {
		const internalLabelTemplate = {
			text: "{value.percent.formatNumber('#.#')}%",
			radius: am4core.percent(-30),
			fill: am4core.color('white'),
			adapter: {
				'textOutput': (text, target) => {
					if (target.dataItem.value === 0) {
						return '';
					}
					return text;
				}
			}
		};
		const outlineConfig = this.outline ? {
			stroke: am4core.color('black'),
			strokeWidth: 2,
			strokeOpacity: 0.5
		} : {};
		this.chartConfig = {
			innerRadius: '33.33%',
			legend: deepMergeObjects(this.bottomLegend ? getBottomLegendConfig() : getCommonLegendConfig(), {
				itemContainers: {
					paddingBottom: LEGEND_ITEM_PADDING,
					paddingTop: LEGEND_ITEM_PADDING,
					template: {
						togglable: !this.disableLegendToggle,
						tabindex: -1
					}
				},
				markers: {
					children: [{
						dy: -1
					}]
				},
				contentAlign: 'center',
				labels: {
					text: `{name} ({value.percent.formatNumber('#.#')}%)`,
					template: {
						fill: am4core.color(this.outline ? this.ThemeService.primaryFontColor : 'black')
					}
				},
				valueLabels: {
					text: ''
				}
			}),
			series: [{
				type: 'PieSeries',
				alignLabels: !this.internalLabels,
				dataFields: {
					category: this.categoryField,
					value: this.valueField
				},
				labels: {
					disabled: !this.internalLabels,
					template: this.internalLabels ? internalLabelTemplate : undefined
				},
				slices: {
					events: {
						hit: event => this.onSliceHitInternal(event)
					},
					template: {
						...outlineConfig,
						tooltipText: this.internalLabels ? '' : "{category}: {value.percent.formatNumber('#.#')}% ({value.value})",
						tabindex: -1
					},
					togglable: false
				},
				ticks: {
					disabled: true
				}
			}]
		};
	}

	public onChartReadyInternal(event: IOnChartReadyData): void {
		this.chart = event.chart;

		this.onChartReady.emit({
			chart: this.chart
		});
	}

	private onSliceHitInternal(event: { target: Slice }): void {
		if (this.onSliceHit) {
			this.onSliceHit.emit({
				data: event.target.dataItem.dataContext
			});
		}
	}
}
